import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
} from 'react';
import {
	ProjectConversionState,
	ProjectConversionContextType,
} from '../models/projectConversionModel';

const ProjectConversionContext = createContext<
	ProjectConversionContextType | undefined
>(undefined);

export const ProjectConversionProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [state, setState] = useState<ProjectConversionState>({
		convertedProjects: new Set(),
		isConverting: new Set(),
	});

	const isProjectConverted = useCallback(
		(projectId: string) => {
			return state.convertedProjects.has(projectId);
		},
		[state.convertedProjects],
	);

	const isProjectConverting = useCallback(
		(projectId: string) => {
			return state.isConverting.has(projectId);
		},
		[state.isConverting],
	);

	const markProjectAsConverted = useCallback((projectId: string) => {
		setState((prev) => {
			const convertedProjects = new Set(prev.convertedProjects);
			convertedProjects.add(projectId);

			const isConverting = new Set(prev.isConverting);
			isConverting.delete(projectId);

			return { convertedProjects, isConverting };
		});
	}, []);

	const markProjectAsConverting = useCallback((projectId: string) => {
		setState((prev) => {
			const isConverting = new Set(prev.isConverting);
			isConverting.add(projectId);

			return {
				...prev,
				isConverting,
			};
		});
	}, []);

	const markProjectAsNotConverting = useCallback((projectId: string) => {
		setState((prev) => {
			const isConverting = new Set(prev.isConverting);
			isConverting.delete(projectId);

			return {
				...prev,
				isConverting,
			};
		});
	}, []);

	const resetProjectState = useCallback((projectId: string) => {
		setState((prev) => {
			const convertedProjects = new Set(prev.convertedProjects);
			convertedProjects.delete(projectId);

			const isConverting = new Set(prev.isConverting);
			isConverting.delete(projectId);

			return { convertedProjects, isConverting };
		});
	}, []);

	const contextValue = useMemo(
		() => ({
			isProjectConverted,
			isProjectConverting,
			markProjectAsConverted,
			markProjectAsConverting,
			markProjectAsNotConverting,
			resetProjectState,
		}),
		[
			isProjectConverted,
			isProjectConverting,
			markProjectAsConverted,
			markProjectAsConverting,
			markProjectAsNotConverting,
			resetProjectState,
		],
	);

	return (
		<ProjectConversionContext.Provider value={contextValue}>
			{children}
		</ProjectConversionContext.Provider>
	);
};

export const useProjectConversionContext = () => {
	const context = useContext(ProjectConversionContext);
	if (context === undefined) {
		throw new Error(
			'useProjectConversionContext must be used within a ProjectConversionProvider',
		);
	}
	return context;
};
